import { speechAuthority } from './SpeechAuthority';

export interface VoiceSpeaker {
  speak: (message: string) => void;
  cancelSpeech?: () => void;
}

/**
 * 🛡️ REPORT COMPLETION VOICE LIFECYCLE
 * Guarantees:
 * 1. For a given report ID, exactly <= 1 voice completion announcement may occur.
 * 2. Re-renders, duplicate state events, and provider reconnects NEVER replay speech.
 * 3. Detects and suppresses acoustic echo of assistant completion announcements.
 */
export class ReportVoiceLifecycle {
  private acknowledgedReportIds = new Set<string>();
  private inFlightReportIds = new Set<string>();
  private lastSpokenText: string | null = null;
  private lastSpokenTime: number = 0;
  private speaker: VoiceSpeaker | null = null;

  constructor(speaker?: VoiceSpeaker) {
    this.speaker = speaker || null;
  }

  public setSpeaker(speaker: VoiceSpeaker | null) {
    this.speaker = speaker;
  }

  /**
   * Acknowledges report completion AT MOST ONCE for a given reportId.
   * Tied strictly to REPORT_ID + COMPLETION_TRANSITION.
   * Returns true if acknowledged, false if already acknowledged or invalid.
   */
  public acknowledgeCompletion(
    report: { id?: string; reportId?: string; spatialContext?: { name?: string } } | null | undefined,
    locationName?: string,
    customSpeaker?: (msg: string) => void
  ): boolean {
    if (!report) return false;
    const id = report.id || report.reportId;
    if (!id) return false;

    // Invariant: for report ID X, completion acknowledgement may occur at most once
    if (this.acknowledgedReportIds.has(id)) {
      return false;
    }

    this.acknowledgedReportIds.add(id);

    const target = locationName || report.spatialContext?.name || 'the target location';
    const msg = `I have compiled an intelligence report for ${target}. The briefing is ready in Spatial Command.`;

    this.lastSpokenText = msg;
    this.lastSpokenTime = Date.now();

    if (customSpeaker) {
      customSpeaker(msg);
    } else if (this.speaker?.speak) {
      this.speaker.speak(msg);
    } else {
      speechAuthority.speak(msg, { source: 'REPORT_COMPLETION' });
    }

    return true;
  }

  /**
   * Checks if an incoming transcript is an acoustic echo of system completion announcements.
   */
  public isEcho(transcript: string): boolean {
    if (!transcript || typeof transcript !== 'string') return false;
    const norm = transcript.trim().toLowerCase();

    // Check against last spoken phrase within 10-second echo window
    if (this.lastSpokenText && (Date.now() - this.lastSpokenTime < 10000)) {
      const spokenNorm = this.lastSpokenText.toLowerCase();
      if (
        norm === spokenNorm ||
        norm.includes('compiled an intelligence report') ||
        norm.includes('briefing is ready in spatial command')
      ) {
        return true;
      }
    }

    // Generic pattern match for assistant report completion phrases
    if (
      norm.startsWith('i have compiled') ||
      norm.startsWith("i've compiled") ||
      norm.startsWith('i compiled') ||
      norm.includes('the briefing is ready in spatial command') ||
      norm.includes('briefing is ready in spatial command')
    ) {
      return true;
    }

    return false;
  }

  public hasAcknowledged(reportId: string): boolean {
    return this.acknowledgedReportIds.has(reportId);
  }

  public reset(): void {
    this.acknowledgedReportIds.clear();
    this.inFlightReportIds.clear();
    this.lastSpokenText = null;
    this.lastSpokenTime = 0;
  }
}

export const reportVoiceLifecycle = new ReportVoiceLifecycle();
